import {
  FORM_UUID_KEY,
  FORM_CONTACT_COUNTRY_KEY,
  getSavedFormReferrer,
  getSessionReferrer,
} from './forms/helpers'
import { v4 as uuidV4 } from 'uuid'
import reactHtmlParser from 'html-react-parser'
import deepMapKeys from 'map-keys-deep'
import axios from 'axios'
import DOMPurify from 'dompurify'
import { BASE_URL } from '../components/store'
import { Project as ReferenceProject } from './reference-projects/detail'
import { Offer } from './offers/detail'
import { t } from './translate'

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    containex?: {
      vat?: Array<{
        country: string
        behavior: number
      }>
      buttonLink?: {
        Url: string
      }
      buttonIcon?: string
      data?: {
        isCtx?: boolean
        isCtxRus?: boolean
        countries?: any[]
        languages?: any[]
        content?: any
        offerLocations?: any[]
        categoryType?: string
        emailSignupUrl?: string
        logo?: string
        newsTags?: any[]
        teaser?: any
        containerTypes?: Record<string, string>
      }
      locale?: string
      turnstileKey?: string
      countries?: any[]
      languages?: any[]
      googleMapsApiKey?: string
      googleMapsId?: string
    }
    // Steuert das JavaScript-Rendering des Yandex-Indexierungsbots.
    // Wird in _ContentPageLayout.cshtml gesetzt, wenn StaticSite aktiv ist.
    YandexRotorSettings?: {
      WaiterEnabled?: boolean
      IsLoaded?: boolean | (() => boolean)
      IsError?: boolean
      FailOnTimeout?: boolean
      NoJsRedirectsToMain?: boolean
    }
  }
}

export const OFFERS_FAV_STORAGE_KEY = 'containexOffers'
export const REFERENCES_FAV_STORAGE_KEY = 'containexReferenceProjects'

interface StoredItem {
  identifier: string
}

export type ProjectType = 'offers' | 'references'
type Project = ReferenceProject | Offer
type ProjectFull = ReferenceProject & Offer

export const getVatConfig = (
  country: string,
): { visible: boolean; required: boolean } => {
  const vatBehavior = window.containex?.vat?.filter(
    (c) => c.country === country,
  )?.[0]?.behavior
  switch (vatBehavior) {
    case 0: // NotMandatoryNotValidated
    case 1: // NotMandatoryValidated
      return {
        visible: true,
        required: false,
      }
    case 2: // MandatoryNotValidated
    case 3: // MandatoryValidated
      return {
        visible: true,
        required: true,
      }
    default:
      return {
        visible: false,
        required: false,
      }
  }
}

export const validateVat = async (
  vatInput: string,
  selectedCountry: string,
) => {
  const vatBehavior = window.containex?.vat?.filter(
    (c) => c.country === selectedCountry,
  )?.[0]?.behavior
  switch (vatBehavior) {
    case 0: // NotMandatoryNotValidated
      return true
    case 1: // NotMandatoryValidated
      return (
        vatInput === '' || (await validateVATNumber(vatInput, selectedCountry))
      )
    case 2: // MandatoryNotValidated
      return vatInput !== ''
    case 3: // MandatoryValidated
      return (
        vatInput !== '' && (await validateVATNumber(vatInput, selectedCountry))
      )
    default:
      return true
  }
}

const validateVATNumber = async (vatNumber: string, countryCode: string) => {
  const fullVatInput = countryCode + vatNumber
  const response = await axios.get(
    `${BASE_URL}/api/forms/validatevatnumber/${fullVatInput}`.toLowerCase(),
  )

  if (response.data) {
    return true
  }
  return false
}

const getMerklisteData = (id, type: ProjectType) => {
  const culture = getCulture()
  const url = isStaticSite()
    ? `/static/content/${id}.${culture.toLocaleLowerCase()}.json?t=${Date.now()}`
    : `${BASE_URL}/api/${type}/get/${id}`
  return axios
    .get<Project>(url, {
      params: { culture },
    })
    .then((res) => {
      return res.data
    })
}

export const getLocalStorageMerklisteID = (type: ProjectType): StoredItem[] =>
  JSON.parse(
    localStorage.getItem(
      type === 'offers' ? OFFERS_FAV_STORAGE_KEY : REFERENCES_FAV_STORAGE_KEY,
    ) || '[]',
  )

const transformFetchedProjects = (project: ProjectFull, type: ProjectType) => {
  const offerPrice = !project.price
    ? t('preis_auf_anfrage')
    : `${project.priceIsStartsAt ? `${t('product.from')}` : ''} ${
        project.currencySymbol || project.currency
      } ${formatPrice(project.price)} ${
        project.isRentPrice ? ` / {t('tag')}` : ''
      }`
  const referenceLocation = project.locationLine
  return {
    ...project,
    subTitle: type === 'offers' ? offerPrice : referenceLocation,
    thumbnail: project.images?.[0]?.url,
  }
}

export const fetchSavedItems = (type: ProjectType) => {
  return Promise.allSettled<Project>(
    getLocalStorageMerklisteID(type).map((item) =>
      getMerklisteData(item.identifier, type),
    ),
  ).then((promises) => {
    const fulfilledPromises = promises.filter(
      (promise) => promise.status === 'fulfilled',
    ) as PromiseFulfilledResult<Project>[]
    return fulfilledPromises
      .map((promise) => promise.value)
      .map((project) => transformFetchedProjects(project as ProjectFull, type))
  })
}

export const parseHtml = (html?: string | null) => {
  const parsedString = typeof html !== 'string' ? '' : reactHtmlParser(html)
  const res = (
    Array.isArray(parsedString) && parsedString.length > 0
      ? parsedString[0]
      : parsedString
  ).toString()
  return DOMPurify.sanitize(res)
}

export const getYoutubeId = (url: string) => {
  let newUrl = window.decodeURIComponent(url)
  newUrl = newUrl.trim().replace(/\s/g, '')

  if (newUrl.includes('watch?v=')) {
    newUrl = newUrl.replace('watch?v=', 'embed/')
  }

  const splitURL = newUrl.split('/')
  const videoId = splitURL[splitURL.length - 1]
  return videoId
}

export const getYoutubeIdAndParams = (
  url: string,
): { videoId: string; params: string } => {
  if (!url) {
    return { videoId: '', params: '' }
  }

  let newUrl = window.decodeURIComponent(url)
  newUrl = newUrl.trim().replace(/\s/g, '')

  // Parse URL to extract video ID and query parameters
  let videoId = ''
  let queryParams = ''

  // Handle watch?v= format (https://www.youtube.com/watch?v=VIDEO_ID&...)
  if (newUrl.includes('watch?v=')) {
    const urlParts = newUrl.split('?')
    const queryString = urlParts[1] || ''
    const searchParams = new URLSearchParams(queryString)

    videoId = searchParams.get('v') || ''

    // Build params string excluding the 'v' parameter
    const paramsList: string[] = []
    searchParams.forEach((value, key) => {
      if (key !== 'v') {
        paramsList.push(`${key}=${value}`)
      }
    })
    queryParams = paramsList.join('&')
  }
  // Handle youtu.be format (https://youtu.be/VIDEO_ID?...)
  else if (newUrl.includes('youtu.be/')) {
    const urlParts = newUrl.split('?')
    const basePart = urlParts[0]
    const queryString = urlParts[1] || ''

    const pathParts = basePart.split('/')
    videoId = pathParts[pathParts.length - 1]

    if (queryString) {
      queryParams = queryString
    }
  }
  // Handle embed format (https://www.youtube.com/embed/VIDEO_ID?...)
  else {
    const urlParts = newUrl.split('?')
    const basePart = urlParts[0]
    const queryString = urlParts[1] || ''

    const pathParts = basePart.split('/')
    videoId = pathParts[pathParts.length - 1]

    if (queryString) {
      queryParams = queryString
    }
  }

  return { videoId, params: queryParams }
}

export const playVideo = () => {
  const video = document.querySelector('.Video') as HTMLElement
  const play = video.querySelector('.Video__play-button')! as HTMLElement
  play?.addEventListener('click', () => {
    play.style.display = 'none'
  })
}

export const saveContactAddressCountry = (formData: any) => {
  const country = formData?.contact?.address?.country
  if (country) {
    sessionStorage.setItem(FORM_CONTACT_COUNTRY_KEY, country)
  }
}

export const saveContactData = (formData: any) => {
  try {
    const isCtx = !!window.containex?.data?.isCtx
    const isCtxRus = !!window.containex?.data?.isCtxRus
    if (isCtxRus || isCtx) {
      const contactInfo = {
        ContactPhone:
          sanitizeTelNumberStrict(formData?.contact?.phoneNumber?.number) || '',
        ContactEmail: formData?.contact?.email || '',
      }
      sessionStorage.setItem('formData', JSON.stringify(contactInfo))
    }
  } catch (error) {
    console.error(error)
  }
}

export const withGlobalFormData = (formData: any) => {
  const uuid = uuidV4()
  sessionStorage.setItem(FORM_UUID_KEY, uuid)
  const urlParams = new URLSearchParams(window.location.search)
  const captchaBypass = urlParams.get('HideTurnstileCaptcha')
  return {
    ...formData,
    ...(formData.attachedFiles && {
      attachedFiles: formData.attachedFiles?.map((file) => ({
        ...file,
        content: file.content.split(',')?.[1],
      })),
    }),
    ...(formData.rentalDetails && {
      rentalDetails: {
        ...formData.rentalDetails,
        ...(formData.rentalDetails.attachedFiles && {
          attachedFiles: formData.rentalDetails.attachedFiles?.map((file) => ({
            ...file,
            content: file.content.split(',')?.[1],
          })),
        }),
      },
    }),
    ...(formData.needAndFinancing && {
      needAndFinancing: {
        ...formData.needAndFinancing,
        ...(formData.needAndFinancing.attachedFiles && {
          attachedFiles: formData.needAndFinancing.attachedFiles?.map(
            (file) => ({
              ...file,
              content: file.content.split(',')?.[1],
            }),
          ),
        }),
      },
    }),
    ...(formData.captcha && {
      captcha: formData.captcha,
    }),
    language: formData.language || getLanguageCode(),
    referrer: getSessionReferrer()
      ? `${getSessionReferrer()}, traid=${uuid}`
      : `traid=${uuid}`,
    formReferrer: getSavedFormReferrer(),
    captchaBypass: captchaBypass,
  }
}

export const getCulture = () => {
  const locale = window.containex?.locale
  if (locale) {
    return locale
  }

  const pieces = window.location.href
    .split('?')[0]
    .replace(location.host, '')
    .replace(location.protocol, '')
    .replace('///', '')
    .split('/')
  return `${pieces[1]}-${pieces[0]}`
}

export const getCaptchaKey = () => window.containex?.turnstileKey ?? ''

const getNumberFormatCulture = () => {
  const culture = getCulture()
  if (culture === 'en-gb' || culture === 'en-uk' || culture === 'en-ie') {
    return culture // format like 1,234,567.-
  }
  return 'it-IT' // format like 1.234.567,-
}
export const getComma = () =>
  (1.5).toLocaleString(getNumberFormatCulture()).split('')[1]

export const formatPrice = (price: number) =>
  getCulture() === 'fr-fr'
    ? `${price?.toLocaleString(getNumberFormatCulture())}`
    : `${price?.toLocaleString(getNumberFormatCulture())}${getComma()}-`

export const formatNumber = (price: number) =>
  `${price?.toLocaleString(getNumberFormatCulture())}`

export function getCountryArray() {
  return (
    (window.containex?.countries || window.containex?.data?.countries || [])
      .map((country) => ({
        ...country,
        name: country.name || country.Name,
        code: (country.code || country.IconCode).toUpperCase(),
      }))
      .filter((country) => country.name) || []
  )
}

export const getLanguageArray = () => {
  return (
    window.containex?.languages ||
    window.containex?.data?.languages ||
    []
  ).map((language) => ({
    ...language,
    name: parseHtml(language.name),
    code: language.code.toUpperCase(),
  }))
}

export const getCountry = () => {
  const countryId = getCulture().split('-')?.[1].toUpperCase()
  return getCountryArray().find((c) => c.code === countryId)?.code
}

export const getCountryObject = () => {
  const countryId = getCulture().split('-')?.[1].toUpperCase()
  return getCountryArray().find((c) => c.code === countryId)
}

export const getLanguage = () => {
  const langId = getCulture().split('-')?.[0]?.toUpperCase()
  return getLanguageArray().find((c) => c.code === langId)?.code
}

export const getIsRuLocale = (): boolean => {
  return getCulture().split('-')?.[0]?.toLowerCase() === 'ru'
}

export const getLanguageCode = () => getCulture().split('-')[0].toUpperCase()

export const getCountryCode = () => {
  const countryId = getCulture().split('-')?.[1].toUpperCase()
  return getCountryArray().find((c) => c.code === countryId)?.phone
}

export const getPhoneCodeByCountry = (countryName) => {
  return getCountryArray().find((c) => c.name === countryName)?.phone
}

export const getCountryByPhoneCode = (phoneCode) => {
  return getCountryArray().find((c) => c.phone === phoneCode)
}

export const getCountryByCode = (code) => {
  return getCountryArray().find((c) => c.code === code)
}

export const getPreloadedData = () =>
  deepMapKeys(
    window.window?.containex?.data?.content || {},
    (key: string) => `${key[0].toLowerCase()}${key.slice(1)}`,
  )

export const getGoogleMapsApiKey = () =>
  window.containex?.googleMapsApiKey ?? ''

export const getGoogleMapsId = () => window.containex?.googleMapsId ?? ''

export const sanitizeTelNumber = (telNumber) =>
  // Allow only digits, plus sign, hyphens, and spaces
  telNumber.replace(/[^\d+\- ]/g, '')

export const sanitizeTelNumberStrict = (telNumber) => {
  // Allow only digits and plus sign
  if (!telNumber) return ''
  return telNumber.replace(/[^+\d]/g, '')
}

export const sanitizeEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  const sanitizedEmail = email.trim()
  if (emailRegex.test(sanitizedEmail)) {
    return sanitizedEmail
  } else {
    console.error('Invalid email format')
    return ''
  }
}

export const sanitizeUrl = (url: string) => {
  if (!url || typeof url !== 'string') {
    console.error('Invalid or empty URL')
    return ''
  }

  const sanitizedUrl = url.trim()
  if (sanitizedUrl.startsWith('/') && !sanitizedUrl.startsWith('//')) {
    return DOMPurify.sanitize(sanitizedUrl)
  }

  try {
    const urlObj = new URL(sanitizedUrl)
    const protocol = urlObj.protocol

    // if (protocol !== 'http:' && protocol !== 'https:') {
    //   console.error('Potentially unsafe URL protocol detected:', protocol)
    //   return ''
    // }

    return DOMPurify.sanitize(sanitizedUrl)
  } catch (error) {
    console.error('Failed to parse URL:', error)
    return ''
  }
}

export const isStaticSite = (): boolean => {
  return import.meta.env.REACT_APP_SITE_MODE === 'static'
}

/**
 * Entfernt saemtliches Markup. Fuer document.title und meta[name=description],
 * wo parseHtml() nicht geeignet ist: dieses gibt bei HTML-haltigen Werten
 * React-Elemente zurueck, deren toString() "[object Object]" liefert.
 */
export const toPlainText = (html?: string | null): string =>
  DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim()

/**
 * Setzt oder erzeugt <link rel="canonical">.
 */
export const setCanonical = (url: string) => {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  if (link.href !== url) {
    link.href = url
  }
}

/**
 * Canonical-URL fuer Overlay-Detailansichten (Referenzen, Mietcontainer).
 * Ohne id die Uebersichtsseite, mit id die Detail-URL.
 * window.location.pathname enthaelt den Trailing Slash bereits, das Ergebnis
 * entspricht damit dem Format der Sitemap-Eintraege.
 */
export const detailCanonical = (id?: string | null): string => {
  const base = window.location.origin + window.location.pathname
  return id ? `${base}?id=${encodeURIComponent(id)}` : base
}

export const setMetaDescription = (text: string) => {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'description'
    document.head.appendChild(meta)
  }
  meta.content = text
}

/**
 * Signalisiert dem Yandex-Renderer, dass der Inhalt geladen ist.
 * No-op, wenn WaiterEnabled nicht gesetzt wurde.
 */
export const signalRotorLoaded = () => {
  if (window.YandexRotorSettings) {
      window.YandexRotorSettings.IsLoaded = true;
  }
}
